import "@components/RichTextEditor/styles.css";
import "jodit/es5/jodit.min.css";

import { i18n, RailsAppProvider, ThemeProvider } from "@bleu.builders/ui";
import { FlipperProvider } from "@contexts/FlipperContext";
import { ProgramProvider, useProgram } from "@contexts/ProgramContext";
import { RailsFlashProvider } from "@contexts/RailsFlashContext";
import { UserProvider, useUser } from "@contexts/UserContext";
import {
  AdminPagePath,
  buildEditPage,
  buildIndexPage,
  buildIndexPageActionButton,
  buildNewPage,
  generateAction,
  generateLoader,
  replaceParams,
} from "@lib/pageBuilder/pagesHelper";
import {
  canManageProgram,
  canManageReceipts,
  canSeeDashboard,
} from "@lib/permissions";
import LayoutSettingsSidebar from "@pages/admin/organization/programs/settings/layout/(components)/LayoutSettingsSidebar";
import ProgramCarouselsSetting from "@pages/admin/organization/programs/settings/layout/carousels";
import ProgramHeaderSetting from "@pages/admin/organization/programs/settings/layout/navigation-menus";
import React, { useEffect } from "react";
import { I18nextProvider } from "react-i18next";
import ReactOnRails from "react-on-rails";
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from "react-router-dom";

import LoginPage from "./(components)/Login";
import { organizationSidebar } from "./(components)/SidebarNav";
import ChallengesPage from "./challenges";
import ChallengeDetailsPage, {
  action as ChallengeDetailsPageAction,
  loader as ChallengeDetailsPageLoader,
} from "./challenges/details";
import ChallengeAnswersTable from "./challenges/details/participant_answers";
import ChallengeUserTable from "./challenges/details/users";
import EditChallengePage, {
  action as EditChallengePageAction,
  loader as EditChallengePageLoader,
} from "./challenges/edit";
import NewChallenge, {
  action as newChallengePageAction,
  loader as newChallengePageLoader,
} from "./challenges/new";
import DashboardPage, { loader as dashboardLoader } from "./dashboard";
import ActionsTable from "./dashboard/actions";
import ChallengesTable from "./dashboard/challenges";
import DashboardOverviewPage from "./dashboard/overview";
import RewardsTable from "./dashboard/rewards";
import Layout, { WithSidebarLayout } from "./Layout";
import OrganizationAdminsPage from "./organization/admins";
import ProgramsPage from "./organization/programs";
import ProgramBrandingLayout, {
  action as programBrandingLayoutAction,
  loader as programBrandingLayoutLoader,
} from "./organization/programs/settings/branding/(components)/Layout";
import ProgramAssetsSettingPage from "./organization/programs/settings/branding/assets";
import ProgramBordersSettingPage from "./organization/programs/settings/branding/borders";
import ProgramBrandingColorsPage from "./organization/programs/settings/branding/colors";
import CustomCSSSetting from "./organization/programs/settings/branding/css";
import ProgramFontsSettingPage from "./organization/programs/settings/branding/fonts";
import ProgramBrandingThemePage from "./organization/programs/settings/branding/theme";
import IntegrationsSidebar from "./organization/programs/settings/integrations/(components)/IntegrationsSidebar";
import IntegrationApiAuth from "./organization/programs/settings/integrations/api_auth";
import IntegrationApiAuthNew from "./organization/programs/settings/integrations/api_auth/new";
import IntegrationAptos from "./organization/programs/settings/integrations/aptos";
import IntegrationOffersReward from "./organization/programs/settings/integrations/offers-reward";
import IntegrationPhotoContests from "./organization/programs/settings/integrations/photo-contests";
import IntegrationSocialNetworkPages from "./organization/programs/settings/integrations/social_network_pages";
import { SettingsSidebar } from "./organization/programs/settings/program/(components)/SettingsSidebar";
import ProgramEnrollmentSetting from "./organization/programs/settings/program/enrollment";
import ProgramGeneralSetting from "./organization/programs/settings/program/general";
import ProgramLabelSetting from "./organization/programs/settings/program/labels";
import ProgramProfileAttributesSetting from "./organization/programs/settings/program/profile_attributes";
import NewProfileAttributePage, {
  action as newProfileAttributePageAction,
  loader as newProfileAttributePageLoader,
} from "./organization/programs/settings/program/profile_attributes/new";
import ProgramPages from "./organization/programs/settings/program/program_pages";
import ProgramPagesNew, {
  action as programPagesNewAction,
  loader as programPagesNewLoader,
} from "./organization/programs/settings/program/program_pages/new";
import PurchaseProofProgramSettings, {
  loader as purchaseProofProgramSettingsLoader,
} from "./organization/programs/settings/program/purchase_proof_settings";
import SEOSetting from "./organization/programs/settings/program/seo";
import ParticipantsPage, {
  action as participantsPageAction,
} from "./participants";
import ParticipantDetailsPage, {
  action as participantDetailsPageAction,
  loader as participantDetailsPageloader,
} from "./participants/details";
import ParticipantActionsTable from "./participants/details/actions";
import ParticipantAttributesTable from "./participants/details/attributes";
import { EditAttributeForm } from "./participants/details/attributes/EditAttributeForm";
import { NewAttributeForm } from "./participants/details/attributes/NewAttributeForm";
import ParticipantTransactionsTable from "./participants/details/blockchain_transactions";
import ParticipantChallengesTable from "./participants/details/challenges";
import ParticipantAnswersTable from "./participants/details/participant_answers";
import ParticipantQuestionsTable from "./participants/details/questions";
import ParticipantRewardsTable from "./participants/details/rewards";
import ReceiptsPage from "./receipts";
import { ReceiptRedirect } from "./receipts/(components)/ReceiptRedirect";
import ReceiptOverviewPage from "./receipts/details";
import RewardsPage from "./rewards";
import RewardDetailsPage, {
  action as rewardDetailsPageAction,
  loader as rewardDetailsPageLoader,
} from "./rewards/details";
import TransactionsTable from "./rewards/details/blockchain_transactions";
import RewardsCouponsTable from "./rewards/details/coupons";
import MiniContestUserTable from "./rewards/details/mini_contest";
import SweepstakesUserTable from "./rewards/details/sweepstakes";
import RewardsUserTable from "./rewards/details/users";
import EditRewardPage, {
  action as editRewardPageAction,
  loader as editRewardPageLoader,
} from "./rewards/edit";
import NewRewardPage, {
  action as newRewardPageAction,
  loader as newRewardPageLoader,
} from "./rewards/new";
import { OrganizationProvider } from "@contexts/OrganizationContext";

const IndexRedirect = () => {
  const program = useProgram();
  const { permissions } = useUser();

  if (canManageReceipts(permissions) && !canSeeDashboard(permissions)) {
    return (
      <Navigate to={`/admin/v2/programs/${program.id}/receipts`} replace />
    );
  }

  return (
    <Navigate
      to={`/admin/v2/programs/${program.id}/dashboard`}
      replace={true}
    />
  );
};

const router = createBrowserRouter([
  {
    path: "/admin/v2/login",
    element: <LoginPage />,
  },
  {
    path: "/admin/v2",
    element: <Layout />,
    children: [
      {
        index: true,
        element: <IndexRedirect />,
      },
      {
        path: "organization",
        element: <WithSidebarLayout items={organizationSidebar} />,
        children: [
          {
            path: "programs",
            element: <ProgramsPage />,
          },
          {
            path: "admins",
            element: <OrganizationAdminsPage />,
            action: OrganizationAdminsPage.action,
            loader: OrganizationAdminsPage.loader,
          },
        ],
      },
      {
        path: "challenges",
        children: [
          {
            path: ":challenge_id",
            element: <ChallengeDetailsPage />,
            loader: ChallengeDetailsPageLoader,
            action: ChallengeDetailsPageAction,
            children: [
              {
                index: true,
                element: <ChallengeUserTable />,
              },
              {
                path: "participant_answers",
                element: <ChallengeAnswersTable />,
              },
            ],
          },
        ],
      },
      {
        path: "receipts",
        children: [
          {
            path: ":receipt_id",
            children: [
              {
                index: true,
                element: <ReceiptOverviewPage />,
              },
            ],
          },
        ],
      },
      {
        path: "challenges/:challenge_id/edit",
        element: <EditChallengePage />,
        loader: EditChallengePageLoader,
        action: EditChallengePageAction,
      },
      {
        path: "rewards",
        children: [
          {
            path: ":reward_id",
            element: <RewardDetailsPage />,
            loader: rewardDetailsPageLoader,
            action: rewardDetailsPageAction,
            children: [
              {
                index: true,
                element: <RewardsUserTable />,
              },
              {
                path: "sweepstakes",
                element: <SweepstakesUserTable />,
                action: SweepstakesUserTable.action,
              },
              {
                path: "coupons",
                element: <RewardsCouponsTable />,
              },
              {
                path: "blockchain_transactions",
                element: <TransactionsTable />,
              },
              {
                path: "mini_contest",
                element: <MiniContestUserTable />,
                action: MiniContestUserTable.action,
              },
            ],
          },
        ],
      },
      {
        path: "rewards/:reward_id/edit",
        element: <EditRewardPage />,
        loader: editRewardPageLoader,
        action: editRewardPageAction,
      },
      {
        path: "programs/:program_id",
        children: [
          {
            index: true,
            element: <Navigate to="./dashboard" />,
          },
          {
            path: "dashboard",
            element: <DashboardPage />,
            loader: dashboardLoader,
            children: [
              {
                index: true,
                element: <DashboardOverviewPage />,
                loader: DashboardOverviewPage.loader,
              },
              {
                path: "actions",
                element: <ActionsTable />,
              },
              {
                path: "challenges",
                element: <ChallengesTable />,
              },
              {
                path: "rewards",
                element: <RewardsTable />,
              },
            ],
          },
          {
            path: "users",
            children: [
              {
                index: true,
                element: <ParticipantsPage />,
                action: participantsPageAction,
              },
              {
                path: ":end_user_id",
                element: <ParticipantDetailsPage />,
                action: participantDetailsPageAction,
                loader: participantDetailsPageloader,
                children: [
                  {
                    index: true,
                    element: <ParticipantActionsTable />,
                    // @ts-expect-error TS(2339) FIXME: Property 'loader' does not exist on type '() => El... Remove this comment to see the full error message
                    loader: ParticipantActionsTable.loader,
                  },
                  {
                    path: "rewards",
                    element: <ParticipantRewardsTable />,
                  },
                  {
                    path: "challenges",
                    element: <ParticipantChallengesTable />,
                  },
                  {
                    path: "questions",
                    element: <ParticipantQuestionsTable />,
                  },
                  {
                    path: "blockchain_transactions",
                    element: <ParticipantTransactionsTable />,
                  },
                  {
                    path: "attributes",
                    children: [
                      { index: true, element: <ParticipantAttributesTable /> },
                      {
                        path: "new",
                        element: <NewAttributeForm />,
                        // loader: NewAttributeForm.loader,
                        loader: generateLoader(
                          "/admin/v2/participants/:end_user_id/participant_profile/new",
                        ),
                        action: generateAction(
                          "/admin/v2/participants/:end_user_id/participant_profile",
                          "Profile Attribute",
                          "create",
                          (data: {
                            data: {
                              program_id: string;
                              participant_id: string;
                            };
                          }) =>
                            replaceParams(AdminPagePath.PARTICIPANT_PROFILE, {
                              program_id: data.data.program_id,
                              participant_id: data.data.participant_id,
                            }),
                        ),
                      },
                      {
                        path: ":participant_profile_id",
                        element: <EditAttributeForm />,
                        loader: generateLoader(
                          "/admin/v2/participant_profile/:participant_profile_id/edit",
                        ),
                        action: generateAction(
                          "/admin/v2/participant_profile/:participant_profile_id",
                          "Profile Attribute",
                          "update",
                          (data: {
                            data: {
                              program_id: string;
                              participant_id: string;
                            };
                          }) =>
                            replaceParams(AdminPagePath.PARTICIPANT_PROFILE, {
                              program_id: data.data.program_id,
                              participant_id: data.data.participant_id,
                            }),
                        ),
                      },
                    ],
                  },
                  {
                    path: "participant_answers",
                    element: <ParticipantAnswersTable />,
                  },
                ],
              },
            ],
          },
          {
            path: "challenges",
            children: [
              {
                index: true,
                element: <ChallengesPage />,
              },
              {
                path: "new",
                element: <NewChallenge />,
                loader: newChallengePageLoader,
                action: newChallengePageAction,
              },
            ],
          },
          {
            path: "receipts",
            children: [
              {
                index: true,
                element: <ReceiptsPage />,
              },
              {
                path: ":receipt_id",
                element: <ReceiptRedirect />,
              },
            ],
          },
          {
            path: "rewards",
            children: [
              {
                index: true,
                element: <RewardsPage />,
              },
              {
                path: "new",
                element: <NewRewardPage />,
                loader: newRewardPageLoader,
                action: newRewardPageAction,
              },
            ],
          },
          {
            path: "settings",
            children: [
              {
                index: true,
                element: <Navigate to="./program" />,
              },
              {
                path: "program",
                element: <SettingsSidebar />,
                children: [
                  {
                    index: true,
                    element: <Navigate to="general" replace />,
                  },
                  {
                    path: "general",
                    element: <ProgramGeneralSetting />,
                  },
                  {
                    path: "profile-attributes",
                    children: [
                      {
                        index: true,
                        element: <ProgramProfileAttributesSetting />,
                      },
                      {
                        path: "new",
                        element: <NewProfileAttributePage />,
                        loader: newProfileAttributePageLoader,
                        action: newProfileAttributePageAction,
                      },
                      {
                        path: ":profile_attribute_id",
                        ...buildEditPage({
                          modelName: "Profile Attribute",
                          actionPath:
                            "/admin/v2/profile_attributes/:profile_attribute_id",
                          loaderPath:
                            "/admin/v2/profile_attributes/:profile_attribute_id/edit",
                          predicate: canManageProgram,
                          redirectAfterAction: (data: {
                            data: { program_id: string };
                          }) =>
                            replaceParams(AdminPagePath.PROFILE_ATTRIBUTES, {
                              program_id: data.data.program_id,
                            }),
                        }),
                      },
                    ],
                  },
                  {
                    path: "seo",
                    element: <SEOSetting />,
                  },
                  {
                    path: "program-pages",
                    children: [
                      { index: true, element: <ProgramPages /> },
                      {
                        path: "new",
                        element: <ProgramPagesNew />,
                        loader: programPagesNewLoader,
                        action: programPagesNewAction,
                      },
                      {
                        path: ":program_page_id",
                        ...buildEditPage({
                          modelName: "Program Page",
                          actionPath:
                            "/admin/v2/program_pages/:program_page_id",
                          loaderPath:
                            "/admin/v2/program_pages/:program_page_id/edit",
                          predicate: canManageProgram,
                          redirectAfterAction: (data: {
                            data: { program_id: string };
                          }) =>
                            replaceParams(AdminPagePath.PROGRAM_PAGES, {
                              program_id: data.data.program_id,
                            }),
                        }),
                      },
                    ],
                  },
                  {
                    path: "labels",
                    element: <ProgramLabelSetting />,
                  },
                  {
                    path: "enrollment",
                    element: <ProgramEnrollmentSetting />,
                  },
                  {
                    path: "purchase-proof",
                    children: [
                      {
                        index: true,
                        element: <Navigate to="products" replace />,
                      },
                      {
                        path: "settings",
                        loader: purchaseProofProgramSettingsLoader,
                        element: <PurchaseProofProgramSettings />,
                      },
                      {
                        path: "products",
                        children: [
                          {
                            index: true,
                            ...buildIndexPage({
                              modelName: "Purchase Proof Products",
                              fetchPath:
                                "/admin/v2/programs/:program_id/purchase_proof_products",
                              action: buildIndexPageActionButton({
                                link: AdminPagePath.PURCHASE_PROOF_PRODUTCS_NEW,
                                text: "Create new Purchase Proof Product",
                                setParams: true,
                              }),
                              predicate: canManageProgram,
                              searchKey: "name",
                            }),
                          },
                          {
                            path: "new",
                            ...buildNewPage({
                              modelName: "Purchase Proof Product",
                              actionPath:
                                "/admin/v2/programs/:program_id/purchase_proof_products",
                              loaderPath:
                                "/admin/v2/programs/:program_id/purchase_proof_products/new",
                              predicate: canManageProgram,
                              redirectAfterAction: (data: {
                                resource: { program_id: string };
                              }) =>
                                replaceParams(
                                  AdminPagePath.PURCHASE_PROOF_PRODUTCS,
                                  {
                                    program_id: data?.resource?.program_id,
                                  },
                                ),
                            }),
                          },
                          {
                            path: ":purchase_proof_product_id",
                            children: [
                              {
                                index: true,
                                element: <Navigate to="edit" />,
                              },
                              {
                                path: "edit",
                                ...buildEditPage({
                                  modelName: "Edit Purchase Proof Product",
                                  actionPath:
                                    "/admin/v2/purchase_proof_products/:purchase_proof_product_id",
                                  loaderPath:
                                    "/admin/v2/purchase_proof_products/:purchase_proof_product_id/edit",
                                  predicate: canManageProgram,
                                  redirectAfterAction: (data: {
                                    resource: { program_id: string };
                                  }) =>
                                    replaceParams(
                                      AdminPagePath.PURCHASE_PROOF_PRODUTCS,
                                      {
                                        program_id: data?.resource?.program_id,
                                      },
                                    ),
                                }),
                              },
                            ],
                          },
                        ],
                      },
                      {
                        path: "merchants",
                        children: [
                          {
                            index: true,
                            ...buildIndexPage({
                              modelName: "Purchase Proof Merchants",
                              fetchPath:
                                "/admin/v2/programs/:program_id/purchase_proof_merchants",
                              action: buildIndexPageActionButton({
                                link: AdminPagePath.PURCHASE_PROOF_MERCHANTS_NEW,
                                text: "Create new Purchase Proof Merchant",
                                setParams: true,
                              }),
                              predicate: canManageProgram,
                              searchKey: "name",
                            }),
                          },
                          {
                            path: "new",
                            ...buildNewPage({
                              modelName: "Purchase Proof Merchant",
                              actionPath:
                                "/admin/v2/programs/:program_id/purchase_proof_merchants",
                              loaderPath:
                                "/admin/v2/programs/:program_id/purchase_proof_merchants/new",
                              predicate: canManageProgram,
                              redirectAfterAction: (data: {
                                resource: { program_id: string };
                              }) =>
                                replaceParams(
                                  AdminPagePath.PURCHASE_PROOF_MERCHANTS,
                                  {
                                    program_id: data?.resource?.program_id,
                                  },
                                ),
                            }),
                          },
                          {
                            path: ":id",
                            children: [
                              {
                                index: true,
                                element: <Navigate to="edit" />,
                              },
                              {
                                path: "edit",
                                ...buildEditPage({
                                  modelName: "Edit Purchase Proof Merchant",
                                  actionPath:
                                    "/admin/v2/purchase_proof_merchants/:id",
                                  loaderPath:
                                    "/admin/v2/purchase_proof_merchants/:id/edit",
                                  predicate: canManageProgram,
                                  redirectAfterAction: (data: {
                                    resource: { program_id: string };
                                  }) =>
                                    replaceParams(
                                      AdminPagePath.PURCHASE_PROOF_MERCHANTS,
                                      {
                                        program_id: data?.resource?.program_id,
                                      },
                                    ),
                                }),
                              },
                            ],
                          },
                        ],
                      },
                    ],
                  },
                  {
                    path: "program_tiers",
                    ...buildNewPage({
                      modelName: "Tier-Based Leveling System",
                      actionPath:
                        "/admin/v2/programs/:program_id/program_tiers",
                      loaderPath:
                        "/admin/v2/programs/:program_id/program_tiers/new",
                      predicate: canManageProgram,
                      redirectAfterAction: (data: { program_id: string }) =>
                        replaceParams(AdminPagePath.TIERS, {
                          program_id: data?.program_id,
                        }),
                    }),
                  },
                  {
                    path: "content-tags",
                    children: [
                      {
                        index: true,
                        ...buildIndexPage({
                          modelName: "Content Tags",
                          fetchPath:
                            "/admin/v2/programs/:program_id/content_tags",
                          predicate: canManageProgram,
                          action: buildIndexPageActionButton({
                            link: AdminPagePath.CONTENT_TAGS_NEW,
                            text: "Create new Content Tag",
                            setParams: true,
                          }),
                          searchKey: "name",
                        }),
                      },
                      {
                        path: "new",
                        ...buildNewPage({
                          modelName: "Content Tag",
                          actionPath:
                            "/admin/v2/programs/:program_id/content_tags",
                          loaderPath:
                            "/admin/v2/programs/:program_id/content_tags/new",
                          predicate: canManageProgram,
                          redirectAfterAction: (data: {
                            resource: { program_id: string };
                          }) =>
                            replaceParams(AdminPagePath.CONTENT_TAGS, {
                              program_id: data?.resource?.program_id,
                            }),
                        }),
                      },
                      {
                        path: ":id",
                        children: [
                          {
                            index: true,
                            element: <Navigate to="edit" />,
                          },
                          {
                            path: "edit",
                            ...buildEditPage({
                              modelName: "Edit Content Tag",
                              actionPath: "/admin/v2/content_tags/:id",
                              loaderPath: "/admin/v2/content_tags/:id/edit",
                              predicate: canManageProgram,
                              redirectAfterAction: (data: {
                                resource: { program_id: string };
                              }) =>
                                replaceParams(AdminPagePath.CONTENT_TAGS, {
                                  program_id: data?.resource?.program_id,
                                }),
                            }),
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                path: "layout",
                element: <LayoutSettingsSidebar />,
                children: [
                  {
                    index: true,
                    element: <Navigate to="navigation-menus" replace />,
                  },
                  {
                    path: "navigation-menus",
                    element: <ProgramHeaderSetting />,
                    loader: ProgramHeaderSetting.loader,
                    action: ProgramHeaderSetting.action,
                  },
                  {
                    path: "carousels",
                    element: <ProgramCarouselsSetting />,
                    loader: ProgramCarouselsSetting.loader,
                    action: ProgramCarouselsSetting.action,
                  },
                ],
              },
              {
                path: "branding",
                element: <ProgramBrandingLayout />,
                loader: programBrandingLayoutLoader,
                action: programBrandingLayoutAction,
                children: [
                  {
                    index: true,
                    element: <Navigate to="colors" replace />,
                  },
                  {
                    path: "colors",
                    element: <ProgramBrandingColorsPage />,
                  },
                  {
                    path: "theme",
                    element: <ProgramBrandingThemePage />,
                  },
                  {
                    path: "assets",
                    element: <ProgramAssetsSettingPage />,
                  },
                  {
                    path: "fonts",
                    element: <ProgramFontsSettingPage />,
                  },
                  {
                    path: "borders",
                    element: <ProgramBordersSettingPage />,
                  },
                  {
                    path: "css",
                    element: <CustomCSSSetting />,
                  },
                ],
              },
              {
                path: "integrations",
                element: <IntegrationsSidebar />,
                children: [
                  {
                    index: true,
                    element: <Navigate to="social-network-pages" replace />,
                  },
                  {
                    path: "social-network-pages",
                    element: <IntegrationSocialNetworkPages />,
                  },
                  {
                    path: "offers-reward",
                    loader: IntegrationOffersReward.loader,
                    element: <IntegrationOffersReward />,
                  },
                  {
                    path: "photo-contests",
                    loader: IntegrationPhotoContests.loader,
                    element: <IntegrationPhotoContests />,
                  },
                  {
                    path: "aptos",
                    loader: IntegrationAptos.loader,
                    element: <IntegrationAptos />,
                  },
                  {
                    path: "api_auths",
                    children: [
                      {
                        index: true,
                        element: <IntegrationApiAuth />,
                      },
                      {
                        path: "new",
                        ...buildNewPage({
                          modelName: "API Key",
                          actionPath:
                            "/admin/v2/programs/:program_id/api_auths",
                          loaderPath:
                            "/admin/v2/programs/:program_id/api_auths/new",
                          redirectAfterAction: false,
                        }),
                        element: <IntegrationApiAuthNew />,
                      },
                    ],
                  },
                  {
                    path: "webhooks",
                    children: [
                      {
                        index: true,
                        ...buildIndexPage({
                          modelName: "Webhook",
                          fetchPath: "/admin/v2/programs/:program_id/webhooks",
                          action: buildIndexPageActionButton({
                            link: `/admin/v2/programs/:program_id/settings/integrations/webhooks/new`,
                            text: "Add Webhook Endpoint",
                            setParams: true,
                          }),
                          searchKey: "url",
                        }),
                      },
                      {
                        path: "new",
                        ...buildNewPage({
                          modelName: "Webhook",
                          actionPath: "/admin/v2/programs/:program_id/webhooks",
                          loaderPath:
                            "/admin/v2/programs/:program_id/webhooks/new",
                          redirectAfterAction: (data: {
                            data: { program_id: string };
                          }) =>
                            replaceParams(AdminPagePath.WEBHOOKS, {
                              program_id: data.data.program_id,
                            }),
                        }),
                      },
                      {
                        path: ":webhook_id",
                        ...buildEditPage({
                          modelName: "Webhook",
                          actionPath: "/admin/v2/webhooks/:webhook_id",
                          loaderPath: "/admin/v2/webhooks/:webhook_id/edit",
                          redirectAfterAction: (data: {
                            data: { program_id: string };
                          }) =>
                            replaceParams(AdminPagePath.WEBHOOKS, {
                              program_id: data.data.program_id,
                            }),
                        }),
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
]);

const App = ({ id = "app", page = null }) => {
  const isServer = typeof window === "undefined";
  const el = isServer ? null : document.getElementById(id);
  const initialPage = page || JSON.parse(el.dataset.page);

  const {
    user,
    userPrograms,
    isAdmin,
    program,
    flash_messages,
    flipper_features,
    user_permissions,
    organization,
  } = initialPage;

  const csrfToken = ReactOnRails.authenticityToken();

  useEffect(() => {
    i18n.default.changeLanguage("en");
  }, []);

  if (program) {
    localStorage.setItem("lastSelectedProgram", program.id);
  }

  return (
    <React.StrictMode>
      {/* @ts-ignore */}
      <I18nextProvider i18n={i18n.default}>
        <RailsAppProvider csrfToken={csrfToken}>
          <ThemeProvider defaultTheme="dark" storageKey="perk-ui-theme">
            <RailsFlashProvider messages={flash_messages}>
              <OrganizationProvider organization={organization}>
                <UserProvider
                  user={user}
                  programs={userPrograms}
                  isAdmin={isAdmin}
                  permissions={user_permissions}
                >
                  <ProgramProvider
                    program={
                      program ||
                      // biome-ignore lint/complexity/useOptionalChain: fix
                      (userPrograms &&
                        userPrograms.find(
                          (p) =>
                            String(p.id) ===
                            localStorage.getItem("lastSelectedProgram"),
                        )) ||
                      // biome-ignore lint/complexity/useOptionalChain: fix
                      (userPrograms && userPrograms[0]) ||
                      {}
                    }
                  >
                    <FlipperProvider flipperFeatures={flipper_features}>
                      <RouterProvider router={router} />
                    </FlipperProvider>
                  </ProgramProvider>
                </UserProvider>
              </OrganizationProvider>
            </RailsFlashProvider>
          </ThemeProvider>
        </RailsAppProvider>
      </I18nextProvider>
    </React.StrictMode>
  );
};

export default App;
